import { useState, useEffect } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';
import { useAuth } from '../context/AuthContext';

export default function Usuarios() {
  const { usuario } = useAuth(); 
  const fincaId = useFincaId(); 
  const esSuperAdmin = usuario?.rol === 'super_admin';

  const [usuarios, setUsuarios] = useState([]);
  const [sectores, setSectores] = useState([]);
  
  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rol, setRol] = useState('admin');
  const [sectorId, setSectorId] = useState('');
  
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    if (fincaId || esSuperAdmin) {
      cargarUsuarios();
      // Solo cargamos sectores si tenemos una finca seleccionada
      if (fincaId) cargarSectores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaId, esSuperAdmin]);

  const cargarUsuarios = async () => {
    try {
      // El interceptor de api/client.js ya agrega el ?finca_id=xxx automáticamente
      const { data } = await api.get('/usuarios');
      setUsuarios(data);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarSectores = async () => {
    try {
      const { data } = await api.get(`/sectores/finca/${fincaId}`);
      setSectores(data);
    } catch (err) {
      console.error(err);
    }
  };

  const abrirModal = (user = null) => {
    setEditando(user);
    setNombre(user?.nombre || '');
    setEmail(user?.email || '');
    setUsername(user?.username || '');
    setPassword('');
    setRol(user?.rol || 'admin');
    setSectorId(user?.sector_id || '');
    setMostrarModal(true);
    setMensaje(null);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setEditando(null);
  };

  const guardarUsuario = async (e) => {
    e.preventDefault();
    setCargando(true);
    setMensaje(null);
    try {
      const payload = { 
        nombre, 
        email, 
        username: username.trim() || undefined,
        rol, 
        sector_id: sectorId || null 
      };
      if (password) payload.password = password;

      // Si es super_admin, permitimos mandar el finca_id (aunque el backend lo infiere si no)
      if (esSuperAdmin && fincaId) {
        payload.finca_id = fincaId;
      }

      if (editando) {
        await api.put(`/usuarios/${editando.id}`, payload);
        setMensaje({ tipo: 'ok', texto: 'Usuario actualizado correctamente' });
      } else {
        if (!password) throw new Error('La contraseña es obligatoria para nuevos usuarios');
        await api.post('/usuarios', payload);
        setMensaje({ tipo: 'ok', texto: 'Usuario creado exitosamente' });
      }
      cerrarModal();
      cargarUsuarios();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || err.message || 'Error al guardar' });
    } finally {
      setCargando(false);
    }
  };

  const cambiarEstado = async (user) => {
    const accion = user.activo ? 'desactivar' : 'activar';
    if (!window.confirm(`¿Estás seguro de ${accion} a ${user.nombre}?`)) return;
    
    try {
      if (user.activo) {
        await api.delete(`/usuarios/${user.id}`);
      } else {
        await api.put(`/usuarios/${user.id}`, { activo: true });
      }
      cargarUsuarios();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cambiar estado');
    }
  };

  const copiarEnlaceTelegram = (token) => {
    if (!token) {
      alert('Este usuario no tiene un token válido para vincular Telegram.');
      return;
    }
    const enlace = `https://t.me/lluchunga_alerts_bot?start=${token}`;
    navigator.clipboard.writeText(enlace);
    alert('¡Enlace copiado! Envíalo al administrador para que lo abra en Telegram.');
  };

  const usuariosFiltrados = usuarios.filter(u => 
    u.nombre.toLowerCase().includes(busqueda.toLowerCase()) || 
    u.email.toLowerCase().includes(busqueda.toLowerCase()) ||
    (u.username && u.username.toLowerCase().includes(busqueda.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Usuarios</h1>
          <p className="text-sm text-gray-500">
            {esSuperAdmin ? 'Mostrando usuarios de la finca seleccionada' : 'Usuarios de tu finca'}
          </p>
        </div>
        <button 
          onClick={() => abrirModal()} 
          className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-blue-800 transition"
        >
          + Nuevo usuario
        </button>
      </div>

      {mensaje && !mostrarModal && (
        <div className={`mb-4 p-3 rounded ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {mensaje.texto}
        </div>
      )}

      <div className="bg-white rounded shadow overflow-hidden">
        <div className="p-3 border-b bg-gray-50 flex items-center">
          <span className="mr-2">🔍</span>
          <input 
            type="text" 
            placeholder="Buscar por nombre, email o usuario..." 
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full max-w-md px-3 py-1.5 border rounded text-sm focus:outline-none focus:border-primary"
          />
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Usuario / Email</th>
                <th className="px-4 py-3 text-left">Rol</th>
                {esSuperAdmin && <th className="px-4 py-3 text-left">Finca</th>} {/* ✅ Solo super_admin ve la finca */}
                <th className="px-4 py-3 text-left">Sector</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-center">Telegram</th>
                <th className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.map(u => (
                <tr key={u.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{u.nombre}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="text-xs font-semibold text-gray-800">@{u.username}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs capitalize font-medium ${
                      u.rol === 'super_admin' ? 'bg-red-100 text-red-800' : 
                      u.rol === 'admin' ? 'bg-purple-100 text-purple-800' : 
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {u.rol}
                    </span>
                  </td>
                  
                  {/* ✅ Solo se muestra si es super_admin */}
                  {esSuperAdmin && (
                    <td className="px-4 py-3 text-gray-600 font-medium">{u.finca_nombre || '-'}</td>
                  )}

                  <td className="px-4 py-3 text-gray-600">
                    {u.sector_nombre ? u.sector_nombre : <span className="text-gray-400 text-xs italic">Todos</span>}
                  </td>
                  
                  <td className="px-4 py-3">
                    {u.activo ? <span className="text-green-600 font-medium">Activo</span> : <span className="text-red-500">Inactivo</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {['admin', 'super_admin', 'biologo'].includes(u.rol) ? (
                      u.telegram_chat_id ? (
                        <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded border border-green-200" title="Ya está vinculado">
                          ✅ Vinculado
                        </span>
                      ) : u.telegram_link_token ? (
                        <button 
                          onClick={() => copiarEnlaceTelegram(u.telegram_link_token)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-bold bg-blue-50 px-2 py-1 rounded border border-blue-200 transition"
                          title="Copiar enlace para vincular"
                        >
                          🔗 Copiar Enlace
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs italic">Sin enlace</span>
                      )
                    ) : (
                      <span className="text-gray-300 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-3">
                      <button onClick={() => abrirModal(u)} className="text-blue-600 hover:underline text-xs font-medium">Editar</button>
                      {/* No permitir desactivar super_admin si no eres super_admin */}
                      {(!u.rol.includes('super_admin') || esSuperAdmin) && (
                        <button 
                          onClick={() => cambiarEstado(u)} 
                          className={`${u.activo ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'} hover:underline text-xs font-medium`}
                        >
                          {u.activo ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editando ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
            
            {mensaje && mostrarModal && (
              <div className={`mb-4 p-2 text-sm rounded ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {mensaje.texto}
              </div>
            )}

            <form onSubmit={guardarUsuario} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nombre Completo</label>
                <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Usuario <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" placeholder="Ej: jperez123" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Contraseña {editando && <span className="text-gray-400 font-normal">(dejar en blanco para no cambiar)</span>}
                </label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" required={!editando} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rol</label>
                  <select value={rol} onChange={(e) => setRol(e.target.value)} className="w-full px-3 py-2 border rounded text-sm">
                    <option value="admin">Admin</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="tecnico">Técnico</option>
                    <option value="operador">Operador</option>
                    <option value="auxiliar">Auxiliar</option>
                    <option value="monitoreo">Monitoreo</option>
                    <option value="parametrista">Parametrista</option>
                    <option value="biologo">Biólogo</option>
                    <option value="gerencia">Gerencia</option>
                    {/* Solo el super_admin puede crear otros super_admin */}
                    {esSuperAdmin && <option value="super_admin">Super Admin</option>}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Sector</label>
                  <select value={sectorId} onChange={(e) => setSectorId(e.target.value)} className="w-full px-3 py-2 border rounded text-sm">
                    <option value="">Todos</option>
                    {sectores.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button type="button" onClick={cerrarModal} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancelar</button>
                <button type="submit" disabled={cargando} className="px-4 py-2 bg-primary text-white rounded shadow hover:bg-blue-800 disabled:opacity-50">
                  {cargando ? 'Guardando...' : (editando ? 'Guardar Cambios' : 'Crear Usuario')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}